import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ScanLine, X, Loader2, Trash2, Check, ImagePlus, AlertTriangle, History } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import BetDateSelector from './BetDateSelector';
import { downscaleImage } from '../utils/downscaleImage';
import ScanProgress from './ScanProgress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATES = ['IL', 'MS', 'TN'];
const STATE_NAMES = { IL: 'Illinois', MS: 'Mississippi', TN: 'Tennessee' };

const MAX_BYTES = 6 * 1024 * 1024;

// localStorage key for "scan & reorder" — last successfully submitted batch.
const LS_KEY = 'lottery-last-scan-batch';
const SAVE_TTL_DAYS = 30;

const loadSavedBatch = () => {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data?.bets?.length || !data?.savedAt) return null;
        const ageMs = Date.now() - new Date(data.savedAt).getTime();
        if (ageMs > SAVE_TTL_DAYS * 24 * 3600 * 1000) {
            localStorage.removeItem(LS_KEY);
            return null;
        }
        return data;
    } catch { return null; }
};

const saveBatch = (bets) => {
    try {
        // Strip volatile fields (id, selected) and keep the actual bet specs
        const clean = bets.map(b => ({
            game_type: b.game_type, digits: b.digits, bet_type: b.bet_type,
            amount: b.amount, state: b.state, drawing_time: b.drawing_time,
        }));
        localStorage.setItem(LS_KEY, JSON.stringify({ bets: clean, savedAt: new Date().toISOString() }));
    } catch { /* quota — non-fatal */ }
};

/**
 * Photo → AI OCR → editable bet list → bulk submit.
 *
 * Player snaps a photo of a handwritten Pick 3 / Pick 4 / Big 3 slip.
 * Backend calls Gemini Vision and returns a list of parsed bets. Player
 * reviews each row, edits inline if AI misread anything, deselects any
 * mistakes, then taps "Submit all picks" to fire each bet through the
 * existing /api/lottery/ticket and /api/lottery/big3 endpoints.
 *
 * Replay shortcut: every successful submit is saved to localStorage so the
 * player can re-fire the same batch with one tap (opens the same review
 * dialog seeded from history — no new Gemini call, no photo needed).
 */
const LotteryPhotoScan = forwardRef(({ onSubmitted, onLastBatch }, ref) => {
    const [open, setOpen] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [previewSrc, setPreviewSrc] = useState('');
    const [bets, setBets] = useState([]);     // [{ id, ...parsed, selected: true }]
    const [notes, setNotes] = useState('');
    const [scanId, setScanId] = useState(null);   // ← persisted scan id from /lottery/scan
    const [savedBatch, setSavedBatch] = useState(null);
    const [isReplay, setIsReplay] = useState(false);
    const [bulkDates, setBulkDates] = useState(null);  // local bulk-apply state; isolated from per-row edits
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    // Refresh the "Replay" button visibility whenever the dialog closes
    useEffect(() => {
        setSavedBatch(loadSavedBatch());
    }, [open]);

    const reset = () => {
        setBets([]); setNotes(''); setPreviewSrc(''); setScanning(false); setSubmitting(false); setIsReplay(false); setBulkDates(null);
    };

    const seedFromSaved = () => {
        const data = loadSavedBatch();
        if (!data) {
            toast.error('No saved slip found.');
            return;
        }
        const seeded = data.bets.map((b, i) => ({
            ...b,
            id: `replay-${Date.now()}-${i}`,
            selected: true,
            confidence: 1,
        }));
        setBets(seeded);
        setNotes(`Replaying ${seeded.length} bets from your last scan.`);
        setPreviewSrc('');
        setIsReplay(true);
        setOpen(true);
    };

    // Imperative API for parents (e.g., LotterySavedSlips library) to
    // seed the review dialog directly with a named slip's bets.
    useImperativeHandle(ref, () => ({
        replayBets(rawBets, label = 'Saved slip') {
            if (!rawBets?.length) return;
            const seeded = rawBets.map((b, i) => ({
                ...b,
                id: `replay-${Date.now()}-${i}`,
                selected: true,
                confidence: 1,
            }));
            // Seed bulkDates BEFORE setBets so the bulk-apply BetDateSelector's
            // mount-default effect doesn't clobber per-bet dates back to [today].
            const seedDates = rawBets[0]?.dates;
            setBulkDates(Array.isArray(seedDates) && seedDates.length > 0 ? seedDates : null);
            setBets(seeded);
            setNotes(`Replaying ${seeded.length} bets from "${label}".`);
            setPreviewSrc('');
            setIsReplay(true);
            setOpen(true);
        },
    }), []);

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
            toast.error('Only JPG, PNG, or WEBP images are supported.');
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error(`Image too large (${Math.round(file.size / 1024)}KB). Max ${MAX_BYTES / 1024 / 1024}MB.`);
            return;
        }
        setScanning(true);
        setBets([]);
        setNotes('');
        // Shrink big phone photos first → much faster upload + OCR on cellular.
        const shrunk = await downscaleImage(file);
        if (!shrunk) { toast.error('Could not read the file'); setScanning(false); return; }
        setPreviewSrc(shrunk.dataUrl);
        try {
            const { data } = await axios.post(`${API}/lottery/scan`, {
                image_base64: shrunk.base64,
                mime_type: shrunk.mime,
            }, { timeout: 90000 });
            const seeded = (data?.bets || []).map((b, i) => ({
                ...b,
                id: `${Date.now()}-${i}`,
                selected: true,
            }));
            setBets(seeded);
            setNotes(data?.notes || '');
            setScanId(data?.scan_id || null);
            if (seeded.length === 0) {
                toast.warning('No bets detected. Try another photo or write the numbers a little bigger.');
            } else {
                toast.success(`Found ${seeded.length} bet${seeded.length === 1 ? '' : 's'} — review & submit.`);
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = (typeof detail === 'string' && detail)
                ? detail
                : (!e.response
                    ? 'Network hiccup — the scan didn’t go through. Check your signal and try again.'
                    : 'Could not scan the photo right now. Please try again.');
            toast.error(msg, { duration: 6000 });
        }
        setScanning(false);
    }, []);

    const updateBet = (id, patch) => {
        setBets(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));
    };

    const removeBet = (id) => {
        setBets(prev => prev.filter(b => b.id !== id));
    };

    const setDigit = (id, idx, val) => {
        const v = val === '' ? null : Math.max(0, Math.min(9, parseInt(val, 10) || 0));
        setBets(prev => prev.map(b => {
            if (b.id !== id) return b;
            const digits = [...b.digits];
            digits[idx] = v === null ? 0 : v;
            return { ...b, digits };
        }));
    };

    const validBets = bets.filter(b => b.selected);

    const submitAll = async () => {
        if (validBets.length === 0) {
            toast.error('Select at least one bet to submit.');
            return;
        }
        setSubmitting(true);
        let okCount = 0;
        let errCount = 0;
        const errors = [];
        for (const b of validBets) {
            try {
                if (b.game_type === 'Big 3') {
                    await axios.post(`${API}/lottery/big3`, {
                        numbers: b.digits,
                        drawing_time: b.drawing_time,
                        ...(b.dates && b.dates.length > 0 ? { dates: b.dates } : {}),
                        ...(scanId ? { scan_id: scanId } : {}),
                    });
                    okCount += 1;
                } else {
                    const pickType = b.game_type === 'Pick 4' ? 4 : 3;
                    // "Ways" mode = explode into N straight tickets covering
                    // every permutation. Picks the dedicated endpoint so the
                    // backend can validate caps per-permutation up-front.
                    const endpoint = b.play_as_ways ? `${API}/lottery/ticket/ways` : `${API}/lottery/ticket`;
                    await axios.post(endpoint, {
                        state: b.state,
                        pick_type: pickType,
                        numbers: b.digits,
                        bet_type: b.play_as_ways ? 'straight' : b.bet_type,
                        amount: Number(b.amount) || 1,
                        drawing_time: b.drawing_time,
                        ...(b.dates && b.dates.length > 0 ? { dates: b.dates } : {}),
                        ...(scanId ? { scan_id: scanId } : {}),
                    });
                    okCount += 1;
                }
            } catch (e) {
                errCount += 1;
                errors.push(`${b.game_type} ${b.digits.join('-')}: ${e.response?.data?.detail || 'failed'}`);
            }
        }
        setSubmitting(false);

        if (okCount > 0) {
            toast.success(`Submitted ${okCount} bet${okCount === 1 ? '' : 's'}!`);
        }
        if (errCount > 0) {
            toast.error(`${errCount} bet${errCount === 1 ? '' : 's'} failed: ${errors.slice(0, 2).join(' · ')}`);
        }
        if (okCount > 0) {
            // Save successful batch for one-tap replay
            saveBatch(validBets);
            // Notify parent (so the Save-To-Library prompt can fire)
            if (onLastBatch) onLastBatch(validBets);
            if (onSubmitted) onSubmitted();
            reset();
            setOpen(false);
        }
    };

    return (
        <>
            <div className="flex flex-col sm:flex-row gap-2">
                <Button
                    onClick={() => setOpen(true)}
                    className="flex-1 h-12 font-black text-base text-white"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #FF007F)' }}
                    data-testid="lottery-scan-open-btn"
                >
                    <ScanLine className="w-5 h-5 mr-2" />
                    Scan Handwritten Ticket
                </Button>
                {savedBatch && (
                    <Button
                        onClick={seedFromSaved}
                        variant="outline"
                        className="flex-1 h-12 font-bold text-sm border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10"
                        data-testid="lottery-scan-replay-btn"
                    >
                        <History className="w-4 h-4 mr-2" />
                        Replay Last Slip · {savedBatch.bets.length} bet{savedBatch.bets.length === 1 ? '' : 's'}
                    </Button>
                )}
            </div>

            <Dialog
                open={open}
                onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}
            >
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lottery-scan-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white text-xl font-black flex items-center gap-2">
                            {isReplay ? <History className="w-5 h-5 text-[#39FF14]" /> : <ScanLine className="w-5 h-5 text-[#FF007F]" />}
                            {isReplay ? 'Replay Last Slip' : 'Scan Handwritten Lottery Slip'}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {isReplay
                                ? 'Same bets as last time. Tap any field to tweak before locking them in.'
                                : 'Snap a photo of your handwritten slip. AI will read the numbers, then you can tap any field to fix anything before we lock the bets in.'}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Upload / preview */}
                    {!previewSrc && !isReplay && (
                        <div className="space-y-3">
                            <div className="rounded-2xl border-2 border-dashed border-white/15 bg-[#0a0a0a] py-10 text-center">
                                <ImagePlus className="w-10 h-10 text-[#A0AAB2] mx-auto mb-2" />
                                <p className="text-sm text-[#A0AAB2] mb-4">Take a clear photo of your slip</p>
                                <div className="flex gap-2 justify-center">
                                    <Button
                                        onClick={() => cameraInputRef.current?.click()}
                                        className="bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold"
                                        data-testid="lottery-scan-camera-btn"
                                    >
                                        <Camera className="w-4 h-4 mr-2" />
                                        Use Camera
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-white/20 text-white hover:bg-white/5"
                                        data-testid="lottery-scan-upload-btn"
                                    >
                                        <ImagePlus className="w-4 h-4 mr-2" />
                                        Pick from device
                                    </Button>
                                </div>
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="lottery-scan-camera-input"
                                />
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="lottery-scan-file-input"
                                />
                            </div>
                            <p className="text-[10px] text-[#666] text-center">
                                Write numbers clearly. Indicate state (IL/MS/TN), Pick 3 or Pick 4, straight/box, and $ amount. Big 3 = $10 flat.
                            </p>
                        </div>
                    )}

                    {(previewSrc || isReplay) && (
                        <div className="space-y-3">
                            {previewSrc && (
                                <div className="relative rounded-lg overflow-hidden border border-white/10">
                                    <img src={previewSrc} alt="Scanned slip" className="w-full max-h-56 object-contain bg-[#0a0a0a]" />
                                    <button
                                        type="button"
                                        onClick={reset}
                                        className="absolute top-2 right-2 bg-black/60 hover:bg-black text-white rounded-full p-1"
                                        aria-label="Remove image"
                                        data-testid="lottery-scan-clear-btn"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}

                            {scanning && (
                                <div className="py-2" data-testid="player-scanning">
                                    <ScanProgress label="Reading your ticket…" />
                                </div>
                            )}

                            {!scanning && bets.length === 0 && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300 flex gap-2">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>No bets detected. Make sure each number is clearly written with state, game type, and amount.</span>
                                </div>
                            )}

                            {!scanning && bets.length > 0 && (
                                <>
                                    {notes && (
                                        <p className="text-[10px] text-[#A0AAB2] italic">AI notes: {notes}</p>
                                    )}
                                    {/* Bulk apply — one tap to set the same draw date(s) on every bet.
                                        Holds its own state so per-row overrides don't visually mutate it. */}
                                    {(() => {
                                        const handleBulk = (d) => {
                                            setBulkDates(d);
                                            setBets(prev => prev.map(b => ({ ...b, dates: d })));
                                        };
                                        return (
                                            <div className="rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-2.5" data-testid="lottery-scan-bulk-date">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-black">
                                                        Bulk · apply to all {bets.length} bets
                                                    </span>
                                                </div>
                                                <BetDateSelector
                                                    dates={bulkDates}
                                                    onChange={handleBulk}
                                                    testIdPrefix="lottery-scan-bulk"
                                                />
                                            </div>
                                        );
                                    })()}
                                    <div className="space-y-2" data-testid="lottery-scan-bets">
                                        <AnimatePresence initial={false}>
                                            {bets.map((b, idx) => (
                                                <motion.div
                                                    key={b.id}
                                                    initial={{ opacity: 0, y: -4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -4 }}
                                                    className={`rounded-lg border p-3 ${b.selected ? 'bg-[#0a0a0a] border-white/15' : 'bg-[#0a0a0a]/50 border-white/5 opacity-60'}`}
                                                    data-testid={`lottery-scan-bet-${idx}`}
                                                >
                                                    <div className="flex items-start gap-2 mb-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={b.selected}
                                                            onChange={(e) => updateBet(b.id, { selected: e.target.checked })}
                                                            className="w-4 h-4 mt-1 accent-[#39FF14]"
                                                            data-testid={`lottery-scan-select-${idx}`}
                                                        />
                                                        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                            {/* Game type */}
                                                            <div>
                                                                <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">Game</label>
                                                                <select
                                                                    value={b.game_type}
                                                                    onChange={(e) => {
                                                                        const gt = e.target.value;
                                                                        const need = gt === 'Pick 4' ? 4 : 3;
                                                                        const digits = b.digits.slice(0, need);
                                                                        while (digits.length < need) digits.push(0);
                                                                        updateBet(b.id, {
                                                                            game_type: gt,
                                                                            digits,
                                                                            bet_type: gt === 'Big 3' ? 'straight' : b.bet_type,
                                                                            amount: gt === 'Big 3' ? 10 : b.amount,
                                                                        });
                                                                    }}
                                                                    className="w-full h-8 bg-[#0e0e0e] border border-white/10 rounded text-white text-xs px-2"
                                                                    data-testid={`lottery-scan-game-${idx}`}
                                                                >
                                                                    <option value="Pick 3">Pick 3</option>
                                                                    <option value="Pick 4">Pick 4</option>
                                                                    <option value="Big 3">Big 3</option>
                                                                </select>
                                                            </div>

                                                            {/* State (hidden for Big 3) */}
                                                            <div>
                                                                <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">State</label>
                                                                {b.game_type === 'Big 3' ? (
                                                                    <span className="block h-8 leading-8 text-[10px] text-[#A0AAB2] italic">All 3</span>
                                                                ) : (
                                                                    <select
                                                                        value={b.state}
                                                                        onChange={(e) => updateBet(b.id, { state: e.target.value })}
                                                                        className="w-full h-8 bg-[#0e0e0e] border border-white/10 rounded text-white text-xs px-2"
                                                                        data-testid={`lottery-scan-state-${idx}`}
                                                                    >
                                                                        {STATES.map(s => <option key={s} value={s}>{STATE_NAMES[s]}</option>)}
                                                                    </select>
                                                                )}
                                                            </div>

                                                            {/* Bet type (hidden for Big 3) */}
                                                            <div>
                                                                <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">Bet</label>
                                                                {b.game_type === 'Big 3' ? (
                                                                    <span className="block h-8 leading-8 text-[10px] text-[#A0AAB2] italic">Straight</span>
                                                                ) : (
                                                                    <select
                                                                        value={b.play_as_ways ? 'ways' : b.bet_type}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (v === 'ways') {
                                                                                updateBet(b.id, { play_as_ways: true, bet_type: 'box' });
                                                                            } else {
                                                                                updateBet(b.id, { play_as_ways: false, bet_type: v });
                                                                            }
                                                                        }}
                                                                        className="w-full h-8 bg-[#0e0e0e] border border-white/10 rounded text-white text-xs px-2"
                                                                        data-testid={`lottery-scan-bettype-${idx}`}
                                                                    >
                                                                        <option value="straight">Straight</option>
                                                                        <option value="box">Box</option>
                                                                        {(b.ways || 1) > 1 && (
                                                                            <option value="ways">Combo ({b.ways}-way)</option>
                                                                        )}
                                                                    </select>
                                                                )}
                                                                {/* Visual cue: AI saw an actual box/circle drawn around the digits */}
                                                                {b.box_drawn && b.game_type !== 'Big 3' && (
                                                                    <p className="text-[9px] text-[#FFD700] mt-0.5 leading-tight">📦 box drawn</p>
                                                                )}
                                                                {b.play_as_ways && (
                                                                    <p className="text-[9px] text-[#39FF14] mt-0.5 leading-tight">Combo · {b.ways}× straight</p>
                                                                )}
                                                            </div>

                                                            {/* Amount */}
                                                            <div>
                                                                <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">$</label>
                                                                {b.game_type === 'Big 3' ? (
                                                                    <span className="block h-8 leading-8 text-xs text-white font-bold">$10.00</span>
                                                                ) : (
                                                                    <input
                                                                        type="number"
                                                                        step="0.5"
                                                                        min="0.5"
                                                                        value={b.amount}
                                                                        onChange={(e) => updateBet(b.id, { amount: e.target.value })}
                                                                        className="w-full h-8 bg-[#0e0e0e] border border-white/10 rounded text-white text-xs px-2"
                                                                        data-testid={`lottery-scan-amount-${idx}`}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeBet(b.id)}
                                                            className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                                                            aria-label="Remove"
                                                            data-testid={`lottery-scan-remove-${idx}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {/* Digits */}
                                                    <div className="flex items-end justify-between gap-2 mt-1">
                                                        <div>
                                                            <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">Numbers</label>
                                                            <div className="flex gap-1">
                                                                {b.digits.map((d, i) => (
                                                                    <input
                                                                        key={i}
                                                                        type="number"
                                                                        min="0"
                                                                        max="9"
                                                                        value={d}
                                                                        onChange={(e) => setDigit(b.id, i, e.target.value)}
                                                                        className="w-10 h-10 text-center text-lg font-bold bg-[#0e0e0e] border border-white/15 rounded text-white"
                                                                        data-testid={`lottery-scan-digit-${idx}-${i}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-[9px] uppercase text-[#A0AAB2] mb-1">Draw</label>
                                                            <select
                                                                value={b.drawing_time}
                                                                onChange={(e) => updateBet(b.id, { drawing_time: e.target.value })}
                                                                className="h-8 bg-[#0e0e0e] border border-white/10 rounded text-white text-xs px-2"
                                                                data-testid={`lottery-scan-draw-${idx}`}
                                                            >
                                                                <option value="midday">Midday</option>
                                                                <option value="evening">Evening</option>
                                                            </select>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="block text-[9px] uppercase text-[#A0AAB2] mb-1">Confidence</span>
                                                            <span className={`text-xs font-mono ${b.confidence > 0.75 ? 'text-[#39FF14]' : b.confidence > 0.5 ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                                                                {Math.round((b.confidence || 0) * 100)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {/* Optional date selector (single or multi-day) */}
                                                    <div className="mt-2 pt-2 border-t border-white/5">
                                                        <BetDateSelector
                                                            dates={b.dates}
                                                            onChange={(d) => updateBet(b.id, { dates: d })}
                                                            testIdPrefix={`lottery-scan-date-${idx}`}
                                                        />
                                                    </div>

                                                    {/* Per-bet Risk → To Win summary so it's obvious which row is straight,
                                                        which is combo, and what each pays at a glance. */}
                                                    {b.game_type !== 'Big 3' && (() => {
                                                        const pt = b.game_type === 'Pick 4' ? 4 : 3;
                                                        const payouts = pt === 3
                                                            ? { straight: 600, box: 150 }
                                                            : { straight: 5000, box: 1250 };
                                                        const amt = Number(b.amount) || 0;
                                                        const ways = Math.max(1, Number(b.ways) || 1);
                                                        let label, risk, perDraw, accent, hint;
                                                        if (b.play_as_ways) {
                                                            label = `COMBO · ${ways}-WAY`;
                                                            accent = 'from-[#FFD700] to-[#FF007F]';
                                                            risk = amt * ways;
                                                            perDraw = amt * payouts.straight;
                                                            hint = `${ways} straight tickets — full straight payout on any matching order`;
                                                        } else if (b.bet_type === 'box') {
                                                            label = `BOX${ways > 1 ? ` · ${ways}-WAY` : ''}`;
                                                            accent = 'from-[#A855F7] to-[#A855F7]';
                                                            risk = amt;
                                                            perDraw = amt * payouts.box;
                                                            hint = 'Wins if any order matches';
                                                        } else {
                                                            label = 'STRAIGHT';
                                                            accent = 'from-[#39FF14] to-[#39FF14]';
                                                            risk = amt;
                                                            perDraw = amt * payouts.straight;
                                                            hint = 'Exact order only';
                                                        }
                                                        const numDays = Math.max(1, (b.dates?.length || 1));
                                                        const riskTotal = risk * numDays;
                                                        return (
                                                            <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2"
                                                                 data-testid={`lottery-scan-towin-${idx}`}>
                                                                <span className={`text-[10px] font-black tracking-wider px-2 py-1 rounded text-black bg-gradient-to-r ${accent}`}>
                                                                    {label}
                                                                </span>
                                                                <div className="flex items-center gap-1.5 text-xs">
                                                                    <span className="text-[#A0AAB2]">Risk</span>
                                                                    <span className="text-white font-mono font-bold">${riskTotal.toFixed(2)}</span>
                                                                    <span className="text-[#A0AAB2]">→</span>
                                                                    <span className="text-[#A0AAB2] uppercase text-[9px] font-bold">Win</span>
                                                                    <span className="text-[#39FF14] font-mono font-black">${perDraw.toFixed(2)}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <DialogFooter className="flex-row gap-2 mt-2">
                        <Button
                            variant="outline"
                            onClick={() => { setOpen(false); reset(); }}
                            className="flex-1 border-white/15 text-white hover:bg-white/5"
                            data-testid="lottery-scan-cancel-btn"
                        >
                            <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button
                            onClick={submitAll}
                            disabled={submitting || scanning || validBets.length === 0}
                            className="flex-1 font-black text-white"
                            style={{ background: 'linear-gradient(135deg, #7C3AED, #FF007F)' }}
                            data-testid="lottery-scan-submit-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting…
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Submit {validBets.length} pick{validBets.length === 1 ? '' : 's'}
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
});

LotteryPhotoScan.displayName = 'LotteryPhotoScan';

export default LotteryPhotoScan;
